import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { canonicalLang, DEFAULT_LANG } from '@shared/config';
import { liveLocale, liveLocaleForMarket, type Market } from '@/marketing/i18n/locales';
import { useSwitchFunnelLocale } from '@features/language-switch';

const MARKETS: Market[] = ['belgium', 'international'];

/**
 * Market switcher for the funnel bar, mirroring the marketing `CountrySwitch`
 * (DIT-399 — the funnel bar had none, so a visitor who reached checkout on the
 * wrong market had to go back to the site to change it).
 *
 * Switching market IS a URL change: it navigates to that market's representative
 * live locale, exactly as `useSwitchMarket` does on the marketing side. That also
 * changes the price list, which is the point — the two are one decision.
 */
export default function MarketSwitch() {
  const { t } = useTranslation('common');
  const { lang } = useParams<{ lang?: string }>();
  const switchLocale = useSwitchFunnelLocale();
  const [open, setOpen] = useState(false);

  const current = canonicalLang(lang) ?? DEFAULT_LANG;
  const market: Market = liveLocale(current)?.market ?? 'international';

  const pick = (next: Market) => {
    setOpen(false);
    if (next !== market) switchLocale(liveLocaleForMarket(next));
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 font-ds text-[14px] font-medium text-ink-soft transition-colors hover:text-ink"
      >
        {t(`nav.market.${market}`)}
        <ChevronDown size={14} strokeWidth={2} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="absolute right-0 top-full z-50 mt-2 w-40 overflow-hidden rounded-2xl border border-line bg-white py-1 shadow-[0_12px_40px_rgba(15,23,42,0.12)]"
          >
            {MARKETS.map((m) => (
              <button
                key={m}
                role="menuitemradio"
                aria-checked={m === market}
                onClick={() => pick(m)}
                className={`block w-full px-4 py-2.5 text-left font-sans text-[14px] transition-colors hover:bg-bg-subtle ${
                  m === market ? 'font-semibold text-primary' : 'font-medium text-ink-soft hover:text-ink'
                }`}
              >
                {t(`nav.market.${m}`)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
